import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Briefcase, Clock, Upload, ArrowUpRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyForm = { name: "", email: "", phone: "", cover_note: "" };

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [applyJob, setApplyJob] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/careers").then(({ data }) => setJobs(data)).catch(() => {});
  }, []);

  const openApply = (job) => { setForm(emptyForm); setResume(null); setApplyJob(job); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      const { data } = await api.post(`/careers/${applyJob.id}/apply`, fd);
      toast.success(data.message);
      setApplyJob(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="careers-page">
      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Careers · Build With Us</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95] max-w-3xl">
              Engineer The <span className="text-ember">Skyline</span> Of Tomorrow
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Join a team of engineers, planners and builders delivering landmark residential, commercial and infrastructure projects. We hire for precision, safety and ambition.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <h2 className="font-heading text-lg uppercase tracking-tight text-white mb-8">Open Positions {jobs.length > 0 && <span className="text-ember">({jobs.length})</span>}</h2>
          </FadeIn>
          <div className="space-y-5">
            {jobs.map((job) => (
              <FadeIn key={job.id}>
                <div className="border border-line bg-graphite p-8 flex flex-wrap items-start justify-between gap-6 hover:border-ember/50 transition-colors duration-200" data-testid={`job-card-${job.id}`}>
                  <div className="max-w-2xl">
                    <h3 className="font-heading text-xl uppercase tracking-tight text-white">{job.title}</h3>
                    <div className="flex flex-wrap gap-5 mt-3 text-xs text-silver uppercase tracking-wider">
                      {job.department && <span className="flex items-center gap-1.5"><Briefcase size={13} className="text-ember" /> {job.department}</span>}
                      {job.location && <span className="flex items-center gap-1.5"><MapPin size={13} className="text-ember" /> {job.location}</span>}
                      <span className="flex items-center gap-1.5"><Clock size={13} className="text-ember" /> {job.job_type}{job.experience ? ` · ${job.experience}` : ""}</span>
                    </div>
                    {job.description && <p className="text-silver text-sm mt-4 leading-relaxed whitespace-pre-line">{job.description}</p>}
                  </div>
                  <Button onClick={() => openApply(job)} data-testid={`apply-btn-${job.id}`}
                    className="rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-wider font-bold text-xs h-12 px-8 group">
                    Apply Now <ArrowUpRight size={14} className="ml-2 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Button>
                </div>
              </FadeIn>
            ))}
            {jobs.length === 0 && (
              <div className="border border-line bg-graphite px-8 py-16 text-center" data-testid="no-jobs-message">
                <p className="text-white font-heading uppercase tracking-tight text-lg">No open positions right now</p>
                <p className="text-silver text-sm mt-3">We're always interested in exceptional people — send your profile to <span className="text-ember">ogkinfra@gmail.com</span>.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Dialog open={!!applyJob} onOpenChange={(o) => !o && setApplyJob(null)}>
        <DialogContent aria-describedby={undefined} className="rounded-none bg-graphite border-line">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-white">Apply — {applyJob?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="job-apply-form">
            {[["name", "Full Name *", "text"], ["email", "Email *", "email"], ["phone", "Phone *", "text"]].map(([k, label, type]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-1.5">{label}</label>
                <Input required type={type} value={form[k]} onChange={set(k)} data-testid={`apply-input-${k}`} className="rounded-none bg-carbon border-line h-11" />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-1.5">Cover Note</label>
              <Textarea value={form.cover_note} onChange={set("cover_note")} rows={3} placeholder="Tell us briefly why you're a fit…" data-testid="apply-cover-input" className="rounded-none bg-carbon border-line" />
            </div>
            <label className="border border-dashed border-line bg-carbon flex items-center justify-center gap-2 h-12 cursor-pointer hover:border-ember transition-colors duration-200 text-silver text-xs" data-testid="apply-resume-input">
              <Upload size={14} className="text-ember" /> {resume ? resume.name : "Attach resume (PDF/DOC, optional)"}
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files[0] || null)} />
            </label>
            <Button type="submit" disabled={busy} data-testid="apply-submit-btn" className="w-full rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-wider font-bold h-12">
              {busy ? "Submitting…" : "Submit Application"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
